import React, { type FC } from "react";

import { Alert } from "~/components/alert";
import { Button } from "~/components/button";
import { type QueryView, Type } from "~/models/dto";

import { useIsPersistNodeFlowMutating } from "../../../queries";
import { DocLink } from "../../react/docLink/DocLink";
import { CloseDrawerButton, DrawerTitle, ExplorationDrawer } from "../../react/drawer/ExplorationDrawer";

import { isEditView } from "./_use-view-ops";
import { ViewForm } from "./_view-form";

const FORM_ID = "view-form";

export interface ViewFormDrawerProps {
	when: undefined | QueryView;
}

export const ViewFormDrawer: FC<ViewFormDrawerProps> = ({ when }) => {
	const isMutating = useIsPersistNodeFlowMutating(Type.QueryView);

	return (
		<ExplorationDrawer
			data-testid="view-form-drawer"
			footer={(view) => (
				<>
					<CloseDrawerButton disabled={isMutating} />
					<Button className="uppercase" form={FORM_ID} loading={isMutating} type="submit" variant="primary">
						{isEditView(view) ? "Update view" : "Create view"}
					</Button>
				</>
			)}
			headerTitle={(view) => <DrawerTitle title={isEditView(view) ? "Edit view properties" : "Save view as..."} />}
			layout="compact"
			when={when}
		>
			{(view, close) => (
				<div className="space-y-6">
					{isEditView(view) ? (
						<FormDescription>You are about to update the properties of the current view.</FormDescription>
					) : (
						<FormDescription>
							You are about to save the current view as a new custom view. All the custom views can be found under the{" "}
							<strong className="font-semibold italic">Views</strong> entry in the main menu.
						</FormDescription>
					)}

					<ViewForm close={close} formId={FORM_ID} view={view} />
				</div>
			)}
		</ExplorationDrawer>
	);
};

const FormDescription: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<Alert>
		{children} To learn more about custom views, please read{" "}
		<DocLink capture="noop" slug="ui-k8s-custom-views">
			our documentation
		</DocLink>
		.
	</Alert>
);
